/**
 * What this app genuinely needs from a browser (DEP-03, MIN §23).
 *
 * Two rules decide what is on this list, and both are about honesty rather than caution.
 *
 * **No user-agent sniffing.** A version string is a claim a browser makes about itself, it is
 * routinely edited, and it answers the wrong question: what matters is whether *this* browser, on
 * *this* machine, can do the things the app does. Every entry below is a probe of the capability
 * itself. Nothing here reads `navigator.userAgent`, and an architecture test refuses it.
 *
 * **Only what has no fallback.** Turning a working feature into a locked door is worse than showing
 * nothing at all: a family who could have played on the keyboard would be sent away by a camera
 * check. So the list is deliberately short, and each entry was found by reading the code for an
 * *unguarded* use:
 *
 *  - `crypto.randomUUID` — mints profile and session identifiers (`ProfilesPage`, `GameStage`), with
 *    no alternative path. Without it a grown-up cannot make a child a profile.
 *  - `AbortController` — how the stage cancels work it no longer needs (`GameStage`).
 *  - a canvas rendering context — Phaser draws the whole game into one (`phaser-host`, `Phaser.AUTO`
 *    accepts WebGL *or* 2D, so the probe accepts either).
 *
 * Deliberately **not** on the list, because each already degrades on its own terms: `localStorage`
 * (the key/value store has a fail-closed path and a sitting can be played as a guest),
 * `getUserMedia` (the whole mission is keyboard-playable, CAM-09), `AudioContext` (the silent
 * engine), and `speechSynthesis` (the pre-baked narration, with per-line TTS only as a fallback).
 *
 * The other half of "never a blank page" is not here and cannot be: a browser too old to parse the
 * module bundle never runs a line of this. That case is answered by the static panel `index.html`
 * carries inside `#root`, which React replaces when it mounts.
 */

/**
 * The globals a probe is allowed to look at.
 *
 * Passed in rather than read from `globalThis` so a test can present a browser that is missing one
 * of them without breaking the environment it is itself running in.
 */
export interface BrowserSupportScope {
  readonly crypto?: { readonly randomUUID?: unknown } | undefined;
  readonly AbortController?: unknown;
  readonly document?: { createElement(tag: string): unknown } | undefined;
}

export interface BrowserRequirement {
  readonly id: string;
  /** What a grown-up is told this is for. Never a browser name, never a version. */
  readonly whatItIsFor: string;
  readonly isPresent: (scope: BrowserSupportScope) => boolean;
}

function canDraw(scope: BrowserSupportScope): boolean {
  const document = scope.document;
  if (document === undefined) return false;
  try {
    const canvas = document.createElement('canvas') as {
      getContext?: (kind: string) => unknown;
    };
    if (typeof canvas.getContext !== 'function') return false;
    // Phaser.AUTO takes whichever it can get, so either one is enough.
    return canvas.getContext('webgl') !== null || canvas.getContext('2d') !== null;
  } catch {
    // A browser that throws while being asked cannot draw the game either.
    return false;
  }
}

export const BROWSER_REQUIREMENTS: readonly BrowserRequirement[] = [
  {
    id: 'random-ids',
    whatItIsFor: 'making a profile for each child',
    isPresent: (scope) => typeof scope.crypto?.randomUUID === 'function',
  },
  {
    id: 'cancellable-work',
    whatItIsFor: 'stopping a mission cleanly when you leave it',
    isPresent: (scope) => typeof scope.AbortController === 'function',
  },
  {
    id: 'game-canvas',
    whatItIsFor: 'drawing the game itself',
    isPresent: canDraw,
  },
];

/** Everything this browser cannot do, in the order the list declares. Empty means playable. */
export function missingRequirements(
  scope: BrowserSupportScope = globalThis as BrowserSupportScope,
): readonly BrowserRequirement[] {
  return BROWSER_REQUIREMENTS.filter((requirement) => !requirement.isPresent(scope));
}
